import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import FiltrosReservas, { FILTROS_RESERVAS_VACIO } from "../../componentesEspeciales/reservasAdmin/filtrosReservas";
import BloqueResenaCompletada from "../../componentesEspeciales/reservasAdmin/bloqueResenaCompletada";

const TABS = [
    { key: "hold",       label: "En proceso de pago",  icon: "bi-hourglass-split", badgeCls: "bg-warning text-dark" },
    { key: "confirmado", label: "Activas",             icon: "bi-calendar-check",  badgeCls: "bg-primary"   },
    { key: "completado", label: "Completadas",          icon: "bi-check-circle",    badgeCls: "bg-success"   },
    { key: "cancelado",  label: "Canceladas",           icon: "bi-x-circle",        badgeCls: "bg-danger"    },
];

const BADGE_ESTADO = {
    hold:       { bg: "rgba(var(--amarillo-warning-rgb), 0.15)",  color: "var(--dorado-oscuro)", label: "En proceso de pago" },
    confirmado: { bg: "rgba(var(--azul-bootstrap-cta-rgb), 0.1)",  color: "var(--azul-bootstrap-cta)", label: "Activa"             },
    completado: { bg: "rgba(var(--verde-bootstrap-rgb), 0.1)",   color: "var(--verde-bootstrap)", label: "Completada"         },
    cancelado:  { bg: "rgba(var(--rojo-error-rgb), 0.1)",   color: "var(--rojo-error)", label: "Cancelada"          },
    expirado:   { bg: "rgba(var(--gris-suave-rgb), 0.1)", color: "var(--gris-suave)", label: "Expirada"           },
};

function fmt(fecha) {
    if (!fecha) return "—";
    return new Date(fecha).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtMonto(n) {
    if (!n) return null;
    return "$" + new Intl.NumberFormat("es-CO").format(n);
}

function nombreUsuario(u) {
    if (!u) return null;
    return [u.nombre, u.apellido].filter(v => v && v !== "NA").join(" ") || `@${u.username}`;
}

function Reservas() {
    const navigate = useNavigate();

    const [tab,      setTab]      = useState("hold");
    const [pagina,   setPagina]   = useState(1);
    const [reservas, setReservas] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [total,    setTotal]    = useState(0);
    const [paginas,  setPaginas]  = useState(1);
    const [conteos,  setConteos]  = useState({});
    const [filtros,  setFiltros]  = useState({ ...FILTROS_RESERVAS_VACIO });

    useEffect(() => {
        (async () => {
            try {
                setCargando(true);
                const res = await axios.post("/listar-reservas-admin", { params: {
                    estado:     tab,
                    pagina,
                    categoria:  filtros.categoria  || undefined,
                    periodo:    filtros.periodo     || undefined,
                    paquete:    filtros.paquete     || undefined,
                    conReclamo: filtros.conReclamo  || undefined,
                }});
                setReservas(res.data.reservas || []);
                setTotal(res.data.total || 0);
                setPaginas(res.data.paginas || 1);
                setConteos(res.data.conteos || {});
            } catch { setReservas([]); }
            finally { setCargando(false); }
        })();
    }, [tab, pagina, filtros.categoria, filtros.periodo, filtros.paquete, filtros.conReclamo]);

    // Tras un envío manual, actualiza esa reserva en la lista sin recargar la página.
    function marcarRecordatorioEnviado(idReserva, datos) {
        setReservas((prev) => prev.map((r) => r._id === idReserva
            ? { ...r, recordatorioResenaManualEn: datos?.recordatorioResenaManualEn, recordatoriosResenaManuales: datos?.recordatoriosResenaManuales }
            : r));
    }

    function cambiarTab(nuevoTab) {
        setTab(nuevoTab);
        setPagina(1);
    }

    function cambiarFiltros(f) {
        setPagina(1);
        setFiltros(f);
    }

    function cambiarPagina(p) {
        setPagina(p);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    return (
        <div className="container-fluid">
            <div className="pt-3 pb-2 mb-4 border-bottom">
                <h1 className="h2 text-dark mb-1">Reservas</h1>
                <p className="text-muted mb-0 small">Vista global de todas las reservas de la plataforma.</p>
            </div>

            {/* Filtros */}
            <FiltrosReservas filtros={filtros} onChange={cambiarFiltros} />

            {/* Tabs */}
            <ul className="nav nav-tabs mb-4">
                {TABS.map(({ key, label, icon, badgeCls }) => (
                    <li className="nav-item" key={key}>
                        <button
                            className={`nav-link ${tab === key ? "active fw-semibold" : ""}`}
                            onClick={() => cambiarTab(key)}
                        >
                            <i className={`bi ${icon} me-1`}></i>{label}
                            {conteos[key] > 0 && (
                                <span className={`badge ${badgeCls} rounded-pill ms-2`} style={{ fontSize: "0.7rem" }}>
                                    {conteos[key]}
                                </span>
                            )}
                        </button>
                    </li>
                ))}
            </ul>

            {/* Contenido */}
            {cargando ? (
                <div className="text-center py-5">
                    <span className="spinner-border text-primary" role="status" />
                </div>
            ) : reservas.length === 0 ? (
                <div className="border rounded-4 p-5 bg-light text-center">
                    <i className="bi bi-calendar-x fs-2 text-muted"></i>
                    <p className="text-muted small mb-0 mt-2">No hay reservas en este estado.</p>
                </div>
            ) : (
                <>
                    <p className="text-muted small mb-3">
                        {total.toLocaleString("es-CO")} reserva{total !== 1 ? "s" : ""}
                        {paginas > 1 && <span className="ms-2">· página {pagina} de {paginas}</span>}
                    </p>

                    <div className="d-flex flex-column gap-2 mb-4">
                        {reservas.map((r) => {
                            const est      = BADGE_ESTADO[r.estado] || {};
                            const comprador = nombreUsuario(r.comprador);
                            const proveedor = nombreUsuario(r.proveedor);
                            return (
                                <div
                                    key={r._id}
                                    className="card border-0 shadow-sm rounded-4"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => navigate("/admin/reserva-admin", { state: { params: { idReserva: r._id } } })}
                                >
                                    <div className="card-body py-3 px-4">
                                        <div className="d-flex align-items-start gap-3 flex-wrap">
                                            <div className="flex-grow-1 min-w-0">
                                                <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                                    <span className="fw-semibold text-dark">{r.nombreServicio || "Servicio"}</span>
                                                    <span className="badge rounded-pill px-2 py-1"
                                                        style={{ background: est.bg, color: est.color, fontSize: "0.72rem" }}>
                                                        {est.label || r.estado}
                                                    </span>
                                                    <span className="badge rounded-pill px-2 py-1"
                                                        style={{ background: "rgba(var(--gris-suave-rgb), 0.08)", color: "var(--gris-suave)", fontSize: "0.72rem" }}>
                                                        {r.tipoPaquete}
                                                    </span>
                                                    {r.reclamo?.abierto && (
                                                        <span className="badge rounded-pill px-2 py-1"
                                                            style={{ background: "rgba(var(--rojo-error-rgb), 0.1)", color: "var(--rojo-error)", fontSize: "0.72rem" }}>
                                                            <i className="bi bi-exclamation-triangle me-1"></i>Reclamo
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-muted small d-flex flex-wrap gap-3">
                                                    <span><i className="bi bi-calendar3 me-1"></i>{fmt(r.fecha || r.createdAt)}</span>
                                                    {r.cobro?.montoTotal > 0 && (
                                                        <span><i className="bi bi-cash me-1"></i>{fmtMonto(r.cobro.montoTotal)}</span>
                                                    )}
                                                    {comprador && (
                                                        <span><i className="bi bi-bag me-1"></i>Comprador: {comprador}</span>
                                                    )}
                                                    {proveedor && (
                                                        <span><i className="bi bi-briefcase me-1"></i>Vendedor: {proveedor}</span>
                                                    )}
                                                </div>
                                            </div>
                                            {r.estado === "completado" ? (
                                                <BloqueResenaCompletada reserva={r} alEnviar={marcarRecordatorioEnviado} />
                                            ) : null}
                                            <i className="bi bi-chevron-right text-muted flex-shrink-0 mt-1"></i>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {paginas > 1 && (
                        <nav>
                            <ul className="pagination pagination-sm justify-content-center gap-1 flex-wrap">
                                <li className={`page-item ${pagina === 1 ? "disabled" : ""}`}>
                                    <button className="page-link rounded-3 border-0 shadow-sm" onClick={() => cambiarPagina(pagina - 1)}>
                                        <i className="bi bi-chevron-left"></i>
                                    </button>
                                </li>
                                {Array.from({ length: paginas }, (_, i) => i + 1)
                                    .filter(p => p === 1 || p === paginas || Math.abs(p - pagina) <= 2)
                                    .reduce((acc, p, idx, arr) => {
                                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                                        acc.push(p);
                                        return acc;
                                    }, [])
                                    .map((p, i) =>
                                        p === "..." ? (
                                            <span key={`e-${i}`} className="text-muted px-1">…</span>
                                        ) : (
                                            <button
                                                key={p}
                                                className={`btn btn-sm rounded-3 px-3 ${p === pagina ? "btn-dark" : "btn-outline-secondary"}`}
                                                onClick={() => cambiarPagina(p)}
                                            >
                                                {p}
                                            </button>
                                        )
                                    )
                                }
                                <li className={`page-item ${pagina === paginas ? "disabled" : ""}`}>
                                    <button className="page-link rounded-3 border-0 shadow-sm" onClick={() => cambiarPagina(pagina + 1)}>
                                        <i className="bi bi-chevron-right"></i>
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    )}
                </>
            )}
        </div>
    );
}

export default Reservas;
